import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, ChevronDown } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useSession } from '../context/SessionContext';
import API_URL from '../config/apiConfig';
import { trackEvent, trackPageView, identifyUser, trackSignUp } from '../services/analytics';

// Native auth - uses standalone Google Auth plugin for iOS/Android (more reliable)
import NativeAuth from '../config/nativeAuth';

// Firebase imports (for web fallback and auth object)
import { 
  auth, 
  RecaptchaVerifier, 
  signInWithPhoneNumber,
  signOut,
  getRedirectResult
} from '../config/firebase';

const BACKEND_URL = API_URL;

// Detect if running on native platform
const isNativeApp = () => {
  return NativeAuth.isNativePlatform();
};

// Get platform name
const getPlatformName = () => {
  return NativeAuth.getPlatform();
};

// Get or create device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('taraanaya_device_id');
  if (!deviceId) {
    deviceId = `device_${uuidv4()}`;
    localStorage.setItem('taraanaya_device_id', deviceId);
  }
  return deviceId;
};

// Check if test device or emergent demo mode
const isTestDevice = () => {
  if (localStorage.getItem('is_test_device') === 'true') return true;
  // Emergent demo (/watch1) allows multiple accounts
  const params = new URLSearchParams(window.location.search);
  if (params.get('source') === 'emergent') return true;
  if (localStorage.getItem('demo_source') === 'emergent') return true;
  return false;
};

// Country codes for phone input
const countryCodes = [
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
];

// Web-First launch: lock auth to Phone OTP only (no Google / Apple / Email).
// Mobile apps (iOS/Android via Capacitor) re-enable Google/Email through their store builds.
const WEB_LAUNCH_PHONE_ONLY = true;

const LoginFirebase = () => {
  const navigate = useNavigate();
  const { updateSessionFromLogin } = useSession();
  
  // Prefill from device-linked account (returning user after uninstall/reinstall).
  // syncStoryStateFromServer() populates these in localStorage on app boot.
  const linkedEmail = typeof window !== 'undefined' ? localStorage.getItem('device_linked_email') : '';
  const linkedPhone = typeof window !== 'undefined' ? localStorage.getItem('device_linked_phone') : '';
  const linkedProvider = typeof window !== 'undefined' ? localStorage.getItem('device_linked_provider') : '';
  const isReturningUser = typeof window !== 'undefined' && localStorage.getItem('returning_user') === 'true';
  
  // Default tab based on prior login provider
  // Web-First launch forces 'phone' regardless of prior provider.
  const defaultTab = WEB_LAUNCH_PHONE_ONLY ? 'phone' : (linkedProvider === 'phone' ? 'phone' : 'email');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [phone, setPhone] = useState(linkedPhone && linkedPhone.startsWith('+91') ? linkedPhone.slice(3) : '');
  const [countryCode, setCountryCode] = useState('+91');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [email, setEmail] = useState(linkedEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [deviceAccountInfo, setDeviceAccountInfo] = useState(null);
  const isMounted = useRef(true);
  const otpRefs = useRef([]);
  // Back-button guard: once `past_story_viewed=true`, the user must NEVER be able to navigate
  // back to the past-story / planets screen. On native, phone back button → App.exitApp().
  // On web/browser, we pin the history entry so pressing "back" just re-asserts /login.
  useEffect(() => {
    const pastViewed = localStorage.getItem('past_story_viewed') === 'true' ||
                       localStorage.getItem('free_story_used') === 'true';
    if (!pastViewed) return;

    let removeNativeListener = null;

    // Native (Capacitor) — hardware back button exits the app
    (async () => {
      try {
        if (window.Capacitor?.isNativePlatform?.()) {
          const { App } = await import('@capacitor/app');
          const handle = await App.addListener('backButton', () => {
            App.exitApp();
          });
          removeNativeListener = () => { try { handle.remove(); } catch (_) {} };
        }
      } catch (e) {}
    })();

    // Web / browser back button — pin the history state on /login so "back" is a no-op.
    const onPopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    try { window.history.pushState(null, '', window.location.href); } catch (_) {}
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      if (removeNativeListener) removeNativeListener();
    };
  }, []);



  // Handle redirect result from Google Sign-In (for mobile/native apps)
  useEffect(() => {
    const handleRedirectResult = async () => {
      const pendingRedirect = localStorage.getItem('google_redirect_pending');
      if (!pendingRedirect) return;
      
      try {
        setIsLoading(true);
        const result = await getRedirectResult(auth);
        
        if (result && result.user) {
          localStorage.removeItem('google_redirect_pending');
          
          if (deviceAccountInfo && deviceAccountInfo.original_provider === 'google') {
            const originalEmail = deviceAccountInfo.original_email?.toLowerCase();
            const loginEmail = result.user.email?.toLowerCase();
            if (originalEmail && loginEmail !== originalEmail) {
              await signOut(auth);
              toast.error(`This device is linked to a different Google account.`, { duration: 5000 });
              setIsLoading(false);
              return;
            }
          }
          
          await handleLoginSuccess(result.user, 'google');
        } else {
          localStorage.removeItem('google_redirect_pending');
          setIsLoading(false);
        }
      } catch (error) {
        localStorage.removeItem('google_redirect_pending');
        setIsLoading(false);
        toast.error('Google login failed. Please try again.');
      }
    };
    
    handleRedirectResult();
  }, [deviceAccountInfo]);

  // Check device account on mount and initialize native auth
  useEffect(() => {
    isMounted.current = true;
    checkDeviceAccount();
    trackPageView('login');
    
    // Initialize native Google Auth on app start
    if (isNativeApp()) {
      // Use IIFE with try/catch instead of .then() to avoid Android Promise issues
      (async () => {
        try {
          await NativeAuth.initializeGoogleAuth();
          console.log('📱 Native Google Auth ready');
        } catch (err) {
          console.error('📱 Failed to init Google Auth:', err);
        }
      })();
    }
    
    // Console functions for test device
    window.markAsTestDevice = async () => {
      localStorage.setItem('is_test_device', 'true');
      try {
        await fetch(`${BACKEND_URL}/api/auth/clear-device-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: getDeviceId() })
        });
      } catch (e) {}
      setDeviceAccountInfo(null);
    };
    
    window.removeTestDevice = () => {
      localStorage.removeItem('is_test_device');
      checkDeviceAccount();
    };
    
    window.clearDeviceAccount = async () => {
      try {
        await fetch(`${BACKEND_URL}/api/auth/clear-device-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: getDeviceId() })
        });
        setDeviceAccountInfo(null);
      } catch (e) {}
    };

    return () => {
      isMounted.current = false;
      // Cleanup reCAPTCHA on unmount
      if (window.recaptchaVerifier) {
        try { 
          window.recaptchaVerifier.clear(); 
        } catch (e) {
          console.log('reCAPTCHA cleanup error:', e);
        }
        window.recaptchaVerifier = null;
      }
      // Also clear the container
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  const checkDeviceAccount = async () => {
    if (isTestDevice()) {
      setDeviceAccountInfo(null);
      return;
    }
    
    try {
      const deviceId = getDeviceId();
      const response = await fetch(`${BACKEND_URL}/api/auth/device-account/${deviceId}`);
      const data = await response.json();
      
      if (data.success && data.has_account) {
        setDeviceAccountInfo(data.account);
      } else {
        setDeviceAccountInfo(null);
      }
    } catch (e) {}
  };

  const formatPhoneForCompare = (phoneNum) => {
    let cleaned = phoneNum.replace(/\D/g, '');
    if (cleaned.startsWith('0')) cleaned = cleaned.substring(1);
    if (cleaned.length === 10) cleaned = '91' + cleaned;
    return '+' + cleaned;
  };

  const validateCredentials = (provider, credential) => {
    if (isTestDevice() || !deviceAccountInfo) {
      return { valid: true };
    }

    const originalProvider = deviceAccountInfo.original_provider;
    const originalPhone = deviceAccountInfo.original_phone || '';
    const originalEmail = deviceAccountInfo.original_email || '';

    // Different login method - block with clear message
    if (originalProvider !== provider) {
      return { 
        valid: false, 
        message: 'Wrong credentials. Please use the credentials you used initially.'
      };
    }

    // Same method but different account
    if (provider === 'phone') {
      const formattedInput = formatPhoneForCompare(credential);
      if (formattedInput !== originalPhone) {
        return { 
          valid: false, 
          message: 'Wrong credentials. Please use the credentials you used initially.'
        };
      }
    } else if (provider === 'email') {
      if (credential.toLowerCase() !== originalEmail.toLowerCase()) {
        return { 
          valid: false, 
          message: 'Wrong credentials. Please use the credentials you used initially.'
        };
      }
    }

    return { valid: true };
  };

  const syncUserToBackend = async (user, provider) => {
    try {
      console.log(`📱 Syncing user to backend - provider: ${provider}, uid: ${user.uid}`);
      const idToken = await user.getIdToken();
      const deviceId = getDeviceId();
      console.log(`📱 Device ID: ${deviceId?.substring(0, 20)}...`);
      
      const response = await fetch(`${BACKEND_URL}/api/auth/firebase-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firebase_uid: user.uid,
          phone: user.phoneNumber || '',
          email: user.email || '',
          name: user.displayName || '',
          picture: user.photoURL || '',
          id_token: idToken,
          device_id: deviceId,
          // Pass pre-login web session_id so backend can link orphan birth_details
          session_id: localStorage.getItem('birth_session_id') || localStorage.getItem('session_id') || null,
          // Also pass the web-generated device_id (may differ from native)
          web_device_id: localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || null,
          provider: provider,
          is_test_device: isTestDevice()
        })
      });
      
      const data = await response.json();
      console.log(`📱 Backend response status: ${response.status}`, data);
      
      if (response.status === 403) {
        await signOut(auth);
        // Show the specific error message from the backend
        const errorMsg = data.message || data.detail || 'This device is linked to another account. Please use your original login credentials.';
        toast.error(errorMsg, { duration: 6000 });
        return { success: false, blocked: true };
      }
      
      if (!response.ok) {
        // Handle other HTTP errors
        const errorMsg = data.detail || data.error || `Server error: ${response.status}`;
        console.error(`📱 Backend error: ${errorMsg}`);
        toast.error(errorMsg, { duration: 5000 });
        return { success: false, data };
      }
      
      if (data.success) {
        updateSessionFromLogin(data);
        localStorage.setItem('user_id', data.user_id);
        return { success: true, data };
      }
      
      // Handle other failures
      if (data.detail || data.error) {
        toast.error(data.detail || data.error || 'Login failed');
      }
      
      return { success: false, data };
    } catch (e) {
      console.error('📱 Sync to backend error:', e);
      toast.error('Connection error. Please check your internet and try again.');
      return { success: false, error: e };
    }
  };

  const handleLoginSuccess = async (user, provider) => {
    const result = await syncUserToBackend(user, provider);
    
    if (result.blocked || !result.success) {
      return;
    }
    
    const userData = result.data;
    
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('firebase_uid', user.uid);
    localStorage.setItem('user_id', userData?.user_id);
    if (user.email) localStorage.setItem('user_email', user.email);
    if (user.phoneNumber) localStorage.setItem('user_phone', user.phoneNumber);
    
    trackEvent('login_success', { provider, user_id: userData?.user_id });
    identifyUser(userData?.user_id, { email: user.email, provider });
    trackSignUp(provider);
    
    toast.success('Welcome to Taraanaya!');
    
    if (userData?.has_birth_details) {
      navigate('/dashboard');
    } else {
      navigate('/onboarding-new');
    }
  };

  // ========== GOOGLE LOGIN ==========
  const handleGoogleLogin = async () => {
    if (deviceAccountInfo && deviceAccountInfo.original_provider !== 'google') {
      const check = validateCredentials('google', '');
      if (!check.valid) {
        toast.error(check.message, { duration: 5000 });
        return;
      }
    }

    setIsLoading(true);
    
    try {
      const result = await NativeAuth.signInWithGoogle();
      
      if (deviceAccountInfo && deviceAccountInfo.original_provider === 'google') {
        const originalEmail = deviceAccountInfo.original_email?.toLowerCase();
        const loginEmail = result.user.email?.toLowerCase();
        if (originalEmail && loginEmail !== originalEmail) {
          await NativeAuth.signOut();
          toast.error('Wrong credentials. Please use the credentials you used initially.', { duration: 5000 });
          return;
        }
      }
      
      await handleLoginSuccess(result.user, 'google');
    } catch (error) {
      console.error('[Login] Google login error:', error);
      
      if (error.code === 'auth/popup-closed-by-user' || error.message?.includes('cancel')) {
        toast.error('Login cancelled');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your internet connection.');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup blocked. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        const domain = window.location.hostname;
        toast.error(`Domain "${domain}" not authorized for Google login. Add it in Firebase Console > Authentication > Settings > Authorized Domains.`, { duration: 10000 });
      } else if (error.code === 'auth/internal-error' || error.message?.includes('INTERNAL ASSERTION')) {
        toast.error('Authentication error. Please refresh the page and try again.');
      } else if (error.code === 'auth/plugin-not-available') {
        toast.error('Authentication not available. Please update the app.');
      } else if (error.message?.includes('timed out')) {
        toast.error('Login timed out. Please try again.');
      } else if (error.message?.includes('not authorized')) {
        const domain = window.location.hostname;
        toast.error(`This domain (${domain}) is not authorized. Add it to Firebase Console > Authorized Domains.`, { duration: 8000 });
      } else {
        toast.error(error.message || 'Google login failed. Try again.');
      }
    } finally {
      // ALWAYS unlock the UI, regardless of success or failure
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // ========== EMAIL LOGIN ==========
  const handleEmailLogin = async () => {
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    const check = validateCredentials('email', email);
    if (!check.valid) {
      toast.error(check.message, { duration: 5000 });
      return;
    }

    setIsLoading(true);
    try {
      const result = await NativeAuth.signInWithEmail(email, password);
      await handleLoginSuccess(result.user, 'email');
    } catch (error) {
      const errCode = error.code || '';
      const errMsg = (error.message || '').toLowerCase();
      const isInvalidCredential = errCode === 'auth/user-not-found' || 
        errCode === 'auth/invalid-credential' ||
        errMsg.includes('invalid') || 
        errMsg.includes('malformed') || 
        errMsg.includes('expired') ||
        errMsg.includes('user-not-found') ||
        errMsg.includes('no user record');
      
      if (isInvalidCredential) {
        try {
          console.log('[Login] User not found or invalid, creating new account...');
          const result = await NativeAuth.createUserWithEmail(email, password);
          toast.success('Account created successfully!');
          await handleLoginSuccess(result.user, 'email');
          return;
        } catch (createError) {
          const createCode = createError.code || '';
          const createMsg = (createError.message || '').toLowerCase();
          if (createCode === 'auth/weak-password' || createMsg.includes('weak')) {
            toast.error('Password must be at least 6 characters');
          } else if (createCode === 'auth/email-already-in-use' || createMsg.includes('already')) {
            toast.error('Incorrect password. Please try again.');
          } else if (createCode === 'auth/invalid-email' || createMsg.includes('invalid email')) {
            toast.error('Invalid email address');
          } else {
            toast.error(createError.message || 'Account creation failed');
          }
          return;
        }
      } else if (errCode === 'auth/wrong-password' || errMsg.includes('wrong password')) {
        toast.error('Incorrect password');
      } else if (errCode === 'auth/invalid-email' || errMsg.includes('invalid email')) {
        toast.error('Invalid email address');
      } else if (errCode === 'auth/too-many-requests' || errMsg.includes('too many')) {
        toast.error('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/plugin-not-available') {
        toast.error('Authentication not available. Please update the app.');
      } else if (errMsg.includes('timed out')) {
        toast.error('Login timed out. Please try again.');
      } else {
        toast.error(error.message || 'Login failed');
      }
    } finally {
      // ALWAYS unlock the UI
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // ========== FORGOT PASSWORD ==========
  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address first');
      return;
    }
    
    setIsLoading(true);
    try {
      await NativeAuth.sendPasswordReset(email);
      toast.success('Password reset email sent! Check your inbox.', { duration: 5000 });
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else {
        toast.error('Failed to send reset email. Try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // ========== PHONE LOGIN ==========
  const setupRecaptcha = () => {
    if (isNativeApp()) {
      return Promise.resolve(null);
    }
    
    return new Promise((resolve, reject) => {
      try {
        // ALWAYS destroy old verifier completely
        if (window.recaptchaVerifier) {
          try { window.recaptchaVerifier.clear(); } catch(e) {}
          window.recaptchaVerifier = null;
        }
        
        // Destroy old container and create a FRESH one
        // This prevents "reCAPTCHA has already been rendered in this element"
        const oldContainer = document.getElementById('recaptcha-container');
        if (oldContainer && oldContainer.parentNode) {
          const freshContainer = document.createElement('div');
          freshContainer.id = 'recaptcha-container';
          oldContainer.parentNode.replaceChild(freshContainer, oldContainer);
        }
        
        // Wait for DOM to settle after container replacement
        setTimeout(() => {
          const container = document.getElementById('recaptcha-container');
          if (!container) {
            reject(new Error('reCAPTCHA container not found'));
            return;
          }
          createNewVerifier(container, resolve, reject);
        }, 300);
      } catch (error) {
        console.error('reCAPTCHA setup error:', error);
        reject(error);
      }
    });
  };
  
  const createNewVerifier = (container, resolve, reject) => {
    try {
      console.log('Creating new reCAPTCHA verifier');
      window.recaptchaVerifier = new RecaptchaVerifier(auth, container, {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          window.recaptchaVerifier = null;
        },
        'error-callback': () => {
          console.error('reCAPTCHA error callback');
          window.recaptchaVerifier = null;
        }
      });
      
      // Add timeout for render
      const renderTimeout = setTimeout(() => {
        console.error('reCAPTCHA render timeout');
        if (window.recaptchaVerifier) {
          try { window.recaptchaVerifier.clear(); } catch(e) {}
          window.recaptchaVerifier = null;
        }
        reject(new Error('reCAPTCHA took too long to load. Please refresh the page.'));
      }, 15000);
      
      window.recaptchaVerifier.render()
        .then((widgetId) => {
          clearTimeout(renderTimeout);
          console.log('reCAPTCHA rendered, widgetId:', widgetId);
          resolve(window.recaptchaVerifier);
        })
        .catch((err) => {
          clearTimeout(renderTimeout);
          console.error('reCAPTCHA render error:', err);
          window.recaptchaVerifier = null;
          reject(err);
        });
    } catch (error) {
      console.error('reCAPTCHA create error:', error);
      reject(error);
    }
  };

  const resetRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (e) {}
      window.recaptchaVerifier = null;
    }
    // Replace container with fresh element to fully reset
    const oldContainer = document.getElementById('recaptcha-container');
    if (oldContainer && oldContainer.parentNode) {
      const freshContainer = document.createElement('div');
      freshContainer.id = 'recaptcha-container';
      oldContainer.parentNode.replaceChild(freshContainer, oldContainer);
    }
  };

  const handleSendOTP = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      toast.error('Enter valid 10-digit number');
      return;
    }
    
    const check = validateCredentials('phone', phone);
    if (!check.valid) {
      toast.error(check.message, { duration: 5000 });
      return;
    }

    setIsLoading(true);
    try {
      // Format phone number with country code
      let formattedPhone = cleanPhone;
      if (formattedPhone.startsWith('0')) formattedPhone = formattedPhone.substring(1);
      
      // Add country code if not present
      const countryDigits = countryCode.replace('+', '');
      if (formattedPhone.length === 10) {
        formattedPhone = countryDigits + formattedPhone;
      }
      formattedPhone = '+' + formattedPhone;
      
      console.log('Sending OTP to:', formattedPhone);
      
      if (isNativeApp()) {
        // Native app — use native phone auth (no reCAPTCHA needed)
        const result = await NativeAuth.signInWithPhone(formattedPhone);
        
        // Android may auto-verify the SMS code (SMS Retriever API)
        // If auto-verified, skip OTP input and login directly
        if (result.autoVerified && result.user) {
          console.log('[Login] Phone auto-verified by Android! Skipping OTP input.');
          toast.success('Phone verified automatically!');
          await handleLoginSuccess(result.user, 'phone');
          return; // Don't show OTP input
        }
        
        setConfirmationResult({ ...result, isNative: true, phone: formattedPhone });
      } else {
        // Web — use Firebase signInWithPhoneNumber with reCAPTCHA
        // Try up to 2 times with fresh reCAPTCHA each time
        let lastError = null;
        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            console.log(`OTP attempt ${attempt}/2`);
            resetRecaptcha(); // Always start fresh
            const appVerifier = await setupRecaptcha();
            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
            setConfirmationResult(confirmation);
            lastError = null;
            break; // Success!
          } catch (attemptError) {
            console.error(`OTP attempt ${attempt} failed:`, attemptError.code, attemptError.message);
            lastError = attemptError;
            resetRecaptcha();
            if (attempt < 2) {
              // Wait before retry
              await new Promise(r => setTimeout(r, 500));
            }
          }
        }
        
        if (lastError) throw lastError;
      }
      
      setShowOtpInput(true);
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('OTP Error:', error);
      resetRecaptcha();
      
      let msg = 'Failed to send OTP';
      if (error.code === 'auth/too-many-requests') msg = 'Too many attempts. Wait a few minutes.';
      else if (error.code === 'auth/invalid-phone-number') msg = 'Invalid phone number. Check and try again.';
      else if (error.code === 'auth/quota-exceeded') msg = 'SMS quota exceeded. Try again later.';
      else if (error.code === 'auth/captcha-check-failed') {
        msg = 'reCAPTCHA verification failed. This domain may not be authorized in Firebase Console. Please add this domain to Firebase > Authentication > Settings > Authorized Domains.';
        console.error('DOMAIN AUTH ISSUE: Add this domain to Firebase Console:', window.location.hostname);
      }
      else if (error.message) msg = error.message;
      toast.error(msg, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Enter 6-digit OTP');
      return;
    }
    if (!confirmationResult) {
      toast.error('Session expired. Request OTP again.');
      setShowOtpInput(false);
      setOtp('');
      return;
    }
    
    setIsLoading(true);
    try {
      let result;
      
      // Native app verification
      if (confirmationResult.isNative) {
        result = await NativeAuth.confirmPhoneOTP(otp, confirmationResult.verificationId);
      } else {
        // Firebase web verification
        result = await confirmationResult.confirm(otp);
      }
      
      await handleLoginSuccess(result.user, 'phone');
    } catch (error) {
      console.error('[Login] OTP verification error:', error);
      
      if (error.code === 'auth/invalid-verification-code') {
        toast.error('Wrong OTP. Please check and try again.');
      } else if (error.code === 'auth/code-expired') {
        toast.error('OTP expired. Request a new one.');
        setShowOtpInput(false);
        setOtp('');
        setConfirmationResult(null);
      } else {
        toast.error(error.message || 'Verification failed');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (otp.length === 6 && confirmationResult && !isLoading) {
      handleVerifyOTP();
    }
  }, [otp]);

  // Handle OTP input — supports SMS auto-fill (iOS QuickType / Android WebOTP) by distributing pasted digits across all 6 boxes
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    // SMS auto-fill: browser pastes entire code (e.g. "123456") into the first focused box
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = digits.concat(Array(6 - digits.length).fill('')).join('').slice(0, 6);
      setOtp(newOtp);
      const lastIdx = Math.min(digits.length, 6) - 1;
      if (lastIdx >= 0 && lastIdx < 5) otpRefs.current[lastIdx + 1]?.focus();
      else otpRefs.current[5]?.blur();
      return;
    }

    const newOtp = otp.split('');
    newOtp[index] = value;
    setOtp(newOtp.join(''));

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // ========== START FRESH / CLEAR CACHE ==========
  const handleStartFresh = async () => {
    try {
      const deviceId = getDeviceId();
      
      // Clear device account in MongoDB
      try {
        await fetch(`${BACKEND_URL}/api/auth/clear-device-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: deviceId })
        });
      } catch (e) {
        console.log('Clear device account error (ignored):', e);
      }

      // Clear all auth-related localStorage keys
      const keysToKeep = ['taraanaya_device_id']; // Keep device ID for tracking
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      // Reset all component state
      setEmail('');
      setPassword('');
      setPhone('');
      setOtp('');
      setShowOtpInput(false);
      setConfirmationResult(null);
      setDeviceAccountInfo(null);
      setIsLoading(false);
      setActiveTab('email');

      // Sign out from Firebase
      try {
        await NativeAuth.signOut();
      } catch (e) {
        console.log('Firebase signout error (ignored):', e);
      }

      toast.success('Cache cleared! You can now login fresh.', { duration: 3000 });
    } catch (error) {
      console.error('Start fresh error:', error);
      toast.error('Failed to clear cache. Please try again.');
    }
  };

  // Floating particles
  const particles = [...Array(25)].map((_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
    color: i % 3 === 0 ? '#92FFF7' : i % 3 === 1 ? '#74B3FF' : '#5F42C9'
  }));

  return (
    <><style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <div 
      data-testid="login-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(16px, 3vh, 24px) clamp(16px, 5vw, 24px)',
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
      }}
    >
      {/* Animated cosmic nebula */}
      <motion.div
        style={{
          position: 'absolute',
          width: 'min(600px, 90vw)',
          height: 'min(600px, 90vw)',
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.1) 0%, rgba(138, 43, 226, 0.06) 50%, transparent 80%)',
          filter: 'blur(80px)',
          left: '50%',
          top: '40%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            left: `${p.x}%`,
            top: `${p.y}%`,
            pointerEvents: 'none',
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* reCAPTCHA container */}
      <div id="recaptcha-container" style={{ position: 'fixed', bottom: '-200px', left: '-200px', zIndex: -1 }}></div>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: '100%',
          maxWidth: 'min(320px, 85vw)',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* Logo - Stars Arc */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ marginBottom: 'clamp(4px, 1vh, 8px)' }}
        >
          <svg 
            width="100%" 
            height="40" 
            viewBox="0 0 200 50" 
            style={{ maxWidth: 'clamp(120px, 35vw, 180px)', display: 'block', margin: '0 auto' }}
          >
            <defs>
              <linearGradient id="loginArcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#92FFF7" stopOpacity="0.7" />
                <stop offset="50%" stopColor="#74B3FF" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#5F42C9" stopOpacity="0.7" />
              </linearGradient>
              <filter id="loginStarGlow">
                <feGaussianBlur stdDeviation="2" result="blur"/>
                <feMerge>
                  <feMergeNode in="blur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            <motion.path
              d="M 30 40 Q 65 16, 100 10 T 170 40"
              stroke="url(#loginArcGrad)"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              filter="url(#loginStarGlow)"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
            
            {[
              { x: 30, y: 40, size: 6, color: '#92FFF7' },
              { x: 65, y: 16, size: 7, color: '#82D9F9' },
              { x: 100, y: 10, size: 9, color: '#74B3FF' },
              { x: 135, y: 16, size: 7, color: '#6A7BE8' },
              { x: 170, y: 40, size: 6, color: '#5F42C9' },
            ].map((star, i) => (
              <motion.path
                key={i}
                d={`
                  M ${star.x} ${star.y - star.size}
                  L ${star.x + star.size * 0.35} ${star.y - star.size * 0.35}
                  L ${star.x + star.size} ${star.y}
                  L ${star.x + star.size * 0.35} ${star.y + star.size * 0.35}
                  L ${star.x} ${star.y + star.size}
                  L ${star.x - star.size * 0.35} ${star.y + star.size * 0.35}
                  L ${star.x - star.size} ${star.y}
                  L ${star.x - star.size * 0.35} ${star.y - star.size * 0.35}
                  Z
                `}
                fill={star.color}
                filter="url(#loginStarGlow)"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
              />
            ))}
          </svg>
        </motion.div>

        {/* Brand Name */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          style={{ textAlign: 'center', marginBottom: 'clamp(16px, 3vh, 32px)', position: 'relative' }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: "'Dancing Script', 'Pacifico', cursive",
              fontSize: 'clamp(26px, 7vw, 36px)',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #92FFF7, #74B3FF, #5F42C9)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'blur(20px)',
              opacity: 0.6,
            }}
          >
            taraanaya
          </div>
          <h1
            style={{
              fontFamily: "'Dancing Script', 'Pacifico', cursive",
              fontSize: 'clamp(26px, 7vw, 36px)',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #92FFF7 0%, #74B3FF 50%, #5F42C9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 25px rgba(146, 255, 247, 0.5))',
              margin: 0,
            }}
          >
            taraanaya
          </h1>
          <p style={{ 
            fontSize: 'clamp(8px, 2.2vw, 10px)', 
            color: '#E2E8F0', 
            letterSpacing: '3px',
            marginTop: 'clamp(4px, 1vh, 8px)',
            fontWeight: 600,
          }}>
            DECISION INTELLIGENCE
          </p>
        </motion.div>

        {/* Device Account Notice */}
        {deviceAccountInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              background: 'rgba(116, 179, 255, 0.1)',
              border: '1px solid rgba(116, 179, 255, 0.2)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#E2E8F0',
              textAlign: 'center',
            }}
          >
            Continue with {deviceAccountInfo.original_provider === 'phone' 
              ? `Phone ••••${deviceAccountInfo.original_phone?.slice(-4)}`
              : deviceAccountInfo.original_provider === 'google'
              ? `Google`
              : `Email`}
          </motion.div>
        )}

        {/* Google Button — hidden on Web-First launch (Phone OTP only) */}
        {!WEB_LAUNCH_PHONE_ONLY && (
        <motion.button
          data-testid="google-login-btn"
          whileTap={{ scale: 0.98 }}
          onClick={handleGoogleLogin}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: 'clamp(10px, 1.8vh, 13px) clamp(14px, 3vw, 20px)',
            marginBottom: 'clamp(10px, 2vh, 16px)',
            background: '#fff',
            border: 'none',
            borderRadius: 'clamp(8px, 2vw, 12px)',
            color: '#1a1a1a',
            fontSize: 'clamp(12px, 3.2vw, 14px)',
            fontWeight: 500,
            cursor: isLoading ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(8px, 2vw, 12px)',
            opacity: isLoading ? 0.7 : 1,
            transition: 'all 0.2s',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {isLoading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="12" cy="12" r="10" stroke="#4285F4" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
              </svg>
              Signing in...
            </span>
          ) : 'Continue with Google'}
        </motion.button>
        )}

        {/* Divider — hidden on Web-First launch (Phone OTP only) */}
        {!WEB_LAUNCH_PHONE_ONLY && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          margin: 'clamp(10px, 2vh, 20px) 0', 
          gap: 'clamp(8px, 3vw, 16px)' 
        }}>
          <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(116, 179, 255, 0.3), transparent)' }} />
          <span style={{ color: 'rgba(226, 232, 240, 0.4)', fontSize: 'clamp(9px, 2.5vw, 11px)', letterSpacing: '2px' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(116, 179, 255, 0.3), transparent)' }} />
        </div>
        )}

        {/* Tabs — hidden on Web-First launch (Phone OTP only) */}
        {!WEB_LAUNCH_PHONE_ONLY && (
        <div style={{ 
          display: 'flex', 
          gap: '4px', 
          marginBottom: 'clamp(12px, 2.5vh, 20px)',
          background: 'rgba(116, 179, 255, 0.08)',
          padding: '3px',
          borderRadius: 'clamp(8px, 2vw, 12px)',
        }}>
          <button
            data-testid="phone-tab"
            onClick={() => { setActiveTab('phone'); setShowOtpInput(false); setOtp(''); }}
            style={{
              flex: 1,
              padding: 'clamp(8px, 1.5vh, 12px)',
              background: activeTab === 'phone' ? 'rgba(116, 179, 255, 0.2)' : 'transparent',
              border: 'none',
              borderRadius: 'clamp(6px, 1.5vw, 10px)',
              color: activeTab === 'phone' ? '#92FFF7' : 'rgba(226, 232, 240, 0.5)',
              fontSize: 'clamp(11px, 3vw, 13px)',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Phone
          </button>
          <button
            data-testid="email-tab"
            onClick={() => { setActiveTab('email'); setShowOtpInput(false); setOtp(''); }}
            style={{
              flex: 1,
              padding: 'clamp(8px, 1.5vh, 12px)',
              background: activeTab === 'email' ? 'rgba(116, 179, 255, 0.2)' : 'transparent',
              border: 'none',
              borderRadius: 'clamp(6px, 1.5vw, 10px)',
              color: activeTab === 'email' ? '#92FFF7' : 'rgba(226, 232, 240, 0.5)',
              fontSize: 'clamp(11px, 3vw, 13px)',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Email
          </button>
        </div>
        )}

        {/* Forms */}
        <AnimatePresence mode="wait">
          {activeTab === 'phone' && (
            <motion.div
              key="phone"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {!showOtpInput ? (
                <>
                  {/* Phone Input with Country Code */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px',
                      background: 'rgba(116, 179, 255, 0.05)',
                      border: '1px solid rgba(116, 179, 255, 0.15)',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      padding: '3px',
                    }}>
                      {/* Country Code Selector */}
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: 'clamp(8px, 1.5vh, 10px) clamp(6px, 1.5vw, 8px)',
                            background: 'rgba(116, 179, 255, 0.1)',
                            border: 'none',
                            borderRadius: 'clamp(6px, 1.5vw, 8px)',
                            color: '#E2E8F0',
                            fontSize: 'clamp(12px, 3.2vw, 14px)',
                            cursor: 'pointer',
                            minWidth: '70px',
                          }}
                        >
                          <span>{countryCodes.find(c => c.code === countryCode)?.flag}</span>
                          <span>{countryCode}</span>
                          <ChevronDown size={14} style={{ opacity: 0.6 }} />
                        </button>
                        
                        {showCountryDropdown && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              marginTop: '4px',
                              background: '#1a2332',
                              border: '1px solid rgba(116, 179, 255, 0.2)',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              zIndex: 100,
                              minWidth: '120px',
                              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                            }}
                          >
                            {countryCodes.map((c) => (
                              <button
                                key={c.code}
                                onClick={() => {
                                  setCountryCode(c.code);
                                  setShowCountryDropdown(false);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  width: '100%',
                                  padding: '10px 12px',
                                  background: countryCode === c.code ? 'rgba(116, 179, 255, 0.15)' : 'transparent',
                                  border: 'none',
                                  color: '#E2E8F0',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                }}
                              >
                                <span>{c.flag}</span>
                                <span>{c.code}</span>
                                <span style={{ opacity: 0.5, marginLeft: 'auto' }}>{c.country}</span>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </div>
                      
                      {/* Phone Number Input */}
                      <input
                        data-testid="phone-input"
                        type="tel"
                        name="taraanaya_phone_no_autofill"
                        autoComplete="off"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="99999 99999"
                        style={{
                          flex: 1,
                          padding: 'clamp(8px, 1.5vh, 12px)',
                          background: 'transparent',
                          border: 'none',
                          color: '#E2E8F0',
                          fontSize: 'clamp(13px, 3.5vw, 15px)',
                          outline: 'none',
                          letterSpacing: '1px',
                        }}
                      />
                    </div>
                  </div>
                  
                  <motion.button
                    data-testid="send-otp-btn"
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSendOTP}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: 'clamp(10px, 2vh, 14px)',
                      background: isLoading 
                        ? 'rgba(116, 179, 255, 0.3)' 
                        : 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)',
                      border: 'none',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      color: '#fff',
                      fontSize: 'clamp(12px, 3.2vw, 14px)',
                      fontWeight: 500,
                      cursor: isLoading ? 'wait' : 'pointer',
                      boxShadow: '0 4px 20px rgba(116, 179, 255, 0.3)',
                    }}
                  >
                    {isLoading ? 'Sending...' : 'Send OTP'}
                  </motion.button>
                </>
              ) : (
                <>
                  <p style={{ 
                    color: 'rgba(226, 232, 240, 0.7)', 
                    fontSize: 'clamp(11px, 3vw, 13px)', 
                    marginBottom: 'clamp(10px, 2vh, 16px)', 
                    textAlign: 'center' 
                  }}>
                    Enter OTP sent to {countryCode} {phone}
                  </p>
                  
                  {/* OTP Input Boxes */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 'clamp(4px, 1.5vw, 8px)', 
                    marginBottom: 'clamp(10px, 2vh, 16px)', 
                    justifyContent: 'center' 
                  }}>
                    {[...Array(6)].map((_, i) => (
                      <input
                        key={i}
                        ref={(el) => otpRefs.current[i] = el}
                        type="text"
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        name={i === 0 ? 'one-time-code' : `otp-${i}`}
                        pattern="[0-9]*"
                        maxLength={1}
                        value={otp[i] || ''}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        style={{
                          width: 'clamp(36px, 10vw, 44px)',
                          height: 'clamp(40px, 7vh, 52px)',
                          textAlign: 'center',
                          fontSize: 'clamp(16px, 4.5vw, 20px)',
                          fontWeight: 600,
                          background: 'rgba(116, 179, 255, 0.08)',
                          border: '1px solid rgba(116, 179, 255, 0.2)',
                          borderRadius: 'clamp(6px, 2vw, 10px)',
                          color: '#92FFF7',
                          outline: 'none',
                        }}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={() => { 
                      setShowOtpInput(false); 
                      setOtp(''); 
                      setConfirmationResult(null); 
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(146, 255, 247, 0.7)',
                      fontSize: 'clamp(11px, 2.8vw, 13px)',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    Change number
                  </button>
                  
                  {/* Verify OTP Button */}
                  <motion.button
                    data-testid="verify-otp-btn"
                    whileTap={{ scale: 0.98 }}
                    onClick={handleVerifyOTP}
                    disabled={isLoading || otp.length !== 6}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: (isLoading || otp.length !== 6)
                        ? 'rgba(116, 179, 255, 0.3)' 
                        : 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: (isLoading || otp.length !== 6) ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 20px rgba(116, 179, 255, 0.3)',
                      opacity: (isLoading || otp.length !== 6) ? 0.6 : 1,
                    }}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Continue'}
                  </motion.button>
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'email' && !WEB_LAUNCH_PHONE_ONLY && (
            <motion.div
              key="email"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginBottom: 'clamp(8px, 1.5vh, 12px)' }}>
                <input
                  data-testid="email-input"
                  type="email"
                  name="taraanaya_email_no_autofill"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  style={{
                    width: '100%',
                    padding: 'clamp(10px, 2vh, 14px) clamp(12px, 3vw, 16px)',
                    background: 'rgba(116, 179, 255, 0.05)',
                    border: '1px solid rgba(116, 179, 255, 0.15)',
                    borderRadius: 'clamp(8px, 2vw, 12px)',
                    color: '#E2E8F0',
                    fontSize: 'clamp(12px, 3.2vw, 14px)',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 'clamp(8px, 1.5vh, 12px)', position: 'relative' }}>
                <input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  name="taraanaya_password_no_autofill"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  style={{
                    width: '100%',
                    padding: 'clamp(10px, 2vh, 14px) clamp(12px, 3vw, 16px)',
                    paddingRight: '44px',
                    background: 'rgba(116, 179, 255, 0.05)',
                    border: '1px solid rgba(116, 179, 255, 0.15)',
                    borderRadius: 'clamp(8px, 2vw, 12px)',
                    color: '#E2E8F0',
                    fontSize: 'clamp(12px, 3.2vw, 14px)',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'rgba(226, 232, 240, 0.4)',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div style={{ textAlign: 'right', marginBottom: 'clamp(8px, 1.5vh, 16px)' }}>
                <button
                  data-testid="forgot-password-btn"
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#74B3FF',
                    fontSize: 'clamp(11px, 2.8vw, 13px)',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <motion.button
                data-testid="email-login-btn"
                whileTap={{ scale: 0.98 }}
                onClick={handleEmailLogin}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 2vh, 14px)',
                  background: isLoading 
                    ? 'rgba(116, 179, 255, 0.3)' 
                    : 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)',
                  border: 'none',
                  borderRadius: 'clamp(8px, 2vw, 12px)',
                  color: '#fff',
                  fontSize: 'clamp(12px, 3.2vw, 14px)',
                  fontWeight: 500,
                  cursor: isLoading ? 'wait' : 'pointer',
                  boxShadow: '0 4px 20px rgba(116, 179, 255, 0.3)',
                }}
              >
                {isLoading ? 'Please wait...' : 'Continue'}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <p style={{ 
          textAlign: 'center', 
          fontSize: 'clamp(9px, 2.5vw, 11px)', 
          color: 'rgba(226, 232, 240, 0.3)', 
          marginTop: 'clamp(8px, 1.5vh, 12px)',
          letterSpacing: '0.5px',
        }}>
          By continuing, you agree to our Terms
        </p>
      </motion.div>

      {/* Bottom tagline */}
      <p style={{
        position: 'absolute',
        bottom: 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px))',
        fontSize: 'clamp(8px, 2vw, 10px)',
        color: 'rgba(226, 232, 240, 0.25)',
        letterSpacing: '3px',
        fontWeight: 600,
      }}>
        PLATFORM POWERED BY ASTROLOGY
      </p>
    </div>
    </>
  );
};

export default LoginFirebase;

